import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '@/hooks/useApi';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { EmptyState } from '@/components/common/EmptyState';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { Wallet, Plus, Eye } from 'lucide-react';
import { formatDate } from '@/lib/formatters';
import { toast } from 'sonner';

interface Settlement {
  id: string;
  tenant_id: string;
  shipment_value: number;
  delivery_fee: number;
  total_amount: number;
  currency: string;
  period_start: string;
  period_end: string;
  status: 'draft' | 'confirmed' | 'paid';
  created_at: string;
}

interface TenantSettlementsProps {
  tenantId: string;
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'draft':
      return 'bg-muted text-muted-foreground border-border';
    case 'confirmed':
      return 'bg-blue-500/10 text-blue-600 border-blue-500/20';
    case 'paid':
      return 'bg-green-500/10 text-green-600 border-green-500/20';
    case 'pending':
      return 'bg-orange-500/10 text-orange-600 border-orange-500/20';
    default:
      return 'bg-muted text-muted-foreground border-border';
  }
};

const getStatusLabel = (status: string) => {
  switch (status) {
    case 'draft':
      return 'مسودة';
    case 'confirmed':
      return 'مؤكدة';
    case 'paid':
      return 'مدفوعة';
    case 'pending':
      return 'معلقة';
    default:
      return status;
  }
};

export function TenantSettlements({ tenantId }: TenantSettlementsProps) {
  const navigate = useNavigate();
  const { get, post, loading } = useApi();
  const [settlements, setSettlements] = useState<Settlement[]>([]);
  const [generating, setGenerating] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);

  useEffect(() => {
    fetchSettlements();
  }, [tenantId]);

  const fetchSettlements = async () => {
    try {
      const response: any = await get(`/api/v1/dashboard/tenants/${tenantId}/settlements`);
      const data = response?.data || response || [];
      setSettlements(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to fetch settlements:', error);
    } finally {
      setHasFetched(true);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      await post(`/api/v1/dashboard/tenants/${tenantId}/settlements/generate`);
      toast.success('تم إنشاء التسوية بنجاح');
      fetchSettlements();
    } catch (error) {
      console.error('Failed to generate settlement:', error);
    } finally {
      setGenerating(false);
    }
  };

  if (!hasFetched || loading) {
    return <LoadingSkeleton type="table" rows={5} />;
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg">التسويات المالية</CardTitle>
        <Button size="sm" onClick={handleGenerate} disabled={generating}>
          <Plus className="h-4 w-4 ms-2" />
          {generating ? 'جاري الإنشاء...' : 'إنشاء تسوية'}
        </Button>
      </CardHeader>
      <CardContent>
        {settlements.length === 0 ? (
          <EmptyState
            icon={<Wallet className="h-12 w-12" />}
            title="لا توجد تسويات"
            description="لم يتم إنشاء أي تسويات لهذا الشريك بعد"
          />
        ) : (
          <div className="space-y-3">
            {settlements.map((settlement) => (
              <div
                key={settlement.id}
                className="flex items-center justify-between p-4 rounded-lg border hover:bg-muted/50 transition-colors cursor-pointer"
                onClick={() => navigate(`/dashboard/settlements/${settlement.id}`)}
              >
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">
                      {formatDate(settlement.period_start)} - {formatDate(settlement.period_end)}
                    </span>
                    <Badge variant="outline" className={getStatusColor(settlement.status)}>
                      {getStatusLabel(settlement.status)}
                    </Badge>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    تاريخ الإنشاء: {formatDate(settlement.created_at)}
                  </span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-start">
                    <p className="text-sm font-semibold">{Number(settlement.shipment_value).toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground">قيمة الشحنات</p>
                  </div>
                  <div className="text-start">
                    <p className="text-sm font-semibold text-blue-600">{Number(settlement.delivery_fee).toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground">رسوم التوصيل</p>
                  </div>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <Eye className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
